import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save, Check } from "lucide-react";

const SPORTS = [
  { id: "radball", name: "Fußball / Radball", desc: "Zwei Halbzeiten, durchlaufende Uhr, Gelbe/Rote Karten.", available: true },
  { id: "handball", name: "Handball", desc: "Zeitstrafen (2 Min), 30-Min-Halbzeiten.", available: false },
  { id: "tabletennis", name: "Tischtennis", desc: "Satzpunkte bis 11, Aufschlagwechsel.", available: false },
  { id: "basketball", name: "Basketball", desc: "Viertel, Shot Clock, Fouls / Powerplay.", available: false },
];

export default function SettingsTab({ snapshot }) {
  const s = snapshot.settings;
  const [defaultDur, setDefaultDur] = useState(s.default_media_duration || 10);
  const [maxVideo, setMaxVideo] = useState(s.max_video_length || 60);
  const [sport, setSport] = useState(s.sport_profile || "radball");
  const [showDatetime, setShowDatetime] = useState(!!s.show_datetime);
  const [autoPublish, setAutoPublish] = useState(s.auto_publish_schedule !== false);
  const [half1, setHalf1] = useState(s.half1_duration ?? 7);
  const [half2, setHalf2] = useState(s.half2_duration ?? 7);
  const [pause, setPause] = useState(s.halftime_break ?? 2);
  const [showTimer, setShowTimer] = useState(s.show_timer !== false);

  useEffect(() => {
    setDefaultDur(s.default_media_duration || 10);
    setMaxVideo(s.max_video_length || 60);
    setSport(s.sport_profile || "radball");
    setShowDatetime(!!s.show_datetime);
    setAutoPublish(s.auto_publish_schedule !== false);
    setHalf1(s.half1_duration ?? 7);
    setHalf2(s.half2_duration ?? 7);
    setPause(s.halftime_break ?? 2);
    setShowTimer(s.show_timer !== false);
  }, [snapshot.active_project_id, s.default_media_duration, s.max_video_length, s.sport_profile, s.show_datetime, s.auto_publish_schedule, s.half1_duration, s.half2_duration, s.halftime_break, s.show_timer]);

  const save = async (patch) => {
    try { await api.put("/settings", patch); toast.success("Gespeichert"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectSport = (id, available) => {
    if (!available) { toast.info("Dieses Profil folgt in einer späteren Version."); return; }
    setSport(id);
    save({ sport_profile: id });
  };

  return (
    <div className="space-y-6">
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-4">Grundeinstellungen</h2>
        <div className="flex flex-wrap gap-4 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">Standard-Bildlaufzeit (s)</span>
            <input type="number" value={defaultDur} onChange={(e) => setDefaultDur(Number(e.target.value))} data-testid="default-duration-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] w-40" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">Max. Videolänge (s)</span>
            <input type="number" value={maxVideo} onChange={(e) => setMaxVideo(Number(e.target.value))} data-testid="max-video-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] w-40" />
          </label>
          <button onClick={() => save({ default_media_duration: defaultDur, max_video_length: maxVideo })} data-testid="save-settings-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-lg hover:bg-white transition-colors">
            <Save className="w-4 h-4" /> Speichern
          </button>
        </div>
        <div className="mt-5">
          <button onClick={() => save({ show_datetime: !showDatetime })} data-testid="toggle-datetime-btn" className="flex items-center gap-3 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-3 hover:border-[#37456b] transition-colors">
            <span className={`w-11 h-6 rounded-full relative transition-colors ${showDatetime ? "bg-[#00F0FF]" : "bg-[#1E2638]"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${showDatetime ? "left-[22px]" : "left-0.5"}`} />
            </span>
            <span className="text-sm">Datum &amp; Uhrzeit neben dem Event anzeigen</span>
          </button>
        </div>
        <div className="mt-3">
          <button onClick={() => { setShowTimer(!showTimer); save({ show_timer: !showTimer }); }} data-testid="toggle-timer-btn" className="flex items-center gap-3 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-3 hover:border-[#37456b] transition-colors">
            <span className={`w-11 h-6 rounded-full relative transition-colors ${showTimer ? "bg-[#00F0FF]" : "bg-[#1E2638]"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${showTimer ? "left-[22px]" : "left-0.5"}`} />
            </span>
            <span className="text-sm">Zeit/Uhr im Scoreboard anzeigen (aus = Zeitlos-Modus mit Phasen-Buttons)</span>
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-[#8A99AD] mr-1">Anzeigemodus:</span>
          {[["standard", "Standard"], ["large", "Groß"]].map(([v, l]) => (
            <button key={v} onClick={() => save({ display_mode: v })} data-testid={`display-mode-${v}`}
              className={`font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg transition-colors ${(s.display_mode || "standard") === v ? "bg-[#E5FF00] text-black" : "bg-[#0A0C10] border border-[#1E2638] text-white hover:border-[#37456b]"}`}>{l}</button>
          ))}
          <span className="text-xs text-[#8A99AD]">Groß = Uhr, Namen &amp; Ticker vergrößert.</span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-[#8A99AD]">„Gleich geht&#39;s los&quot;-Vorschau:</span>
          <input type="number" min="0" key={`kl-${snapshot.active_project_id}-${s.kickoff_lead}`} defaultValue={s.kickoff_lead ?? 10} onBlur={(e) => Number(e.target.value) !== (s.kickoff_lead ?? 10) && save({ kickoff_lead: Number(e.target.value) })} data-testid="kickoff-lead-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2 outline-none focus:border-[#00F0FF] w-24" />
          <span className="text-sm text-[#8A99AD]">Minuten vor Anpfiff (0 = aus)</span>
        </div>
        <div className="mt-3">
          <button onClick={() => save({ demo_mode: !(s.demo_mode === true) })} data-testid="toggle-demo-btn" className="flex items-center gap-3 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-3 hover:border-[#37456b] transition-colors">
            <span className={`w-11 h-6 rounded-full relative transition-colors ${s.demo_mode === true ? "bg-[#00F0FF]" : "bg-[#1E2638]"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${s.demo_mode === true ? "left-[22px]" : "left-0.5"}`} />
            </span>
            <span className="text-sm">Demo-Modus (zeigt alle Tabellen, Gruppen &amp; Spieltage – auch leere; sonst nur gefüllte &amp; aktueller Tag)</span>
          </button>
        </div>
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Zeiterfassung (Halbzeiten)</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Standard-Spielzeiten für dieses Turnier. Countdown je Halbzeit, danach optionale Nachspielzeit, dann „Halbzeit" bis zum Start der 2. Halbzeit. Pro Spiel überschreibbar.</p>
        <div className="flex flex-wrap gap-4 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">1. Halbzeit (Min)</span>
            <input type="number" value={half1} onChange={(e) => setHalf1(Number(e.target.value))} data-testid="half1-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] w-32" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">2. Halbzeit (Min)</span>
            <input type="number" value={half2} onChange={(e) => setHalf2(Number(e.target.value))} data-testid="half2-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] w-32" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">Halbzeitpause (Min)</span>
            <input type="number" value={pause} onChange={(e) => setPause(Number(e.target.value))} data-testid="halftime-break-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] w-32" />
          </label>
          <button onClick={() => save({ half1_duration: half1, half2_duration: half2, halftime_break: pause })} data-testid="save-halftimes-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-lg hover:bg-white transition-colors">
            <Save className="w-4 h-4" /> Speichern
          </button>
        </div>
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Spielplan-Automatik</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Wenn eine Gruppe komplett ist, werden Folgespiele mit den echten Teams gefüllt.</p>
        <button onClick={() => { setAutoPublish(!autoPublish); save({ auto_publish_schedule: !autoPublish }); }} data-testid="toggle-autopublish-btn" className="flex items-center gap-3 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-3 hover:border-[#37456b] transition-colors">
          <span className={`w-11 h-6 rounded-full relative transition-colors ${autoPublish ? "bg-[#00F0FF]" : "bg-[#1E2638]"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${autoPublish ? "left-[22px]" : "left-0.5"}`} />
          </span>
          <span className="text-sm">{autoPublish ? "Automatisch veröffentlichen" : "Erst manuell freigeben (Freigabe-Hinweis oben)"}</span>
        </button>
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Displays</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Lege fest, welche Spielfelder jedes Display zeigt (/display, /display1 … /display4). Ohne Auswahl zeigt ein Display nur Werbung und schaltet automatisch auf die Anzeigetafel, sobald ein Spiel auf einem seiner Felder läuft – und 30 s nach Halbzeit/Spielende zurück auf Werbung.</p>
        {(snapshot.fields || []).length === 0 ? (
          <p className="text-sm text-[#8A99AD]">Erst Spielfelder anlegen (Reiter „Tabellen").</p>
        ) : (
          <div className="space-y-3">
            {[["main", "/display"], ["1", "/display1"], ["2", "/display2"], ["3", "/display3"], ["4", "/display4"]].map(([key, route]) => {
              const cur = s.displays?.[key] ?? (key === "main" ? (snapshot.fields || []).map((f) => f.id) : []);
              const toggle = (fid) => {
                const set = new Set(cur);
                set.has(fid) ? set.delete(fid) : set.add(fid);
                const next = { ...(s.displays || {}) };
                if (key === "main" && s.displays?.main === undefined) next.main = (snapshot.fields || []).map((f) => f.id);
                next[key] = [...set];
                save({ displays: next });
              };
              return (
                <div key={key} className="flex flex-wrap items-center gap-4 border border-[#1E2638] rounded-lg p-3" data-testid={`display-cfg-${key}`}>
                  <span className="font-display font-bold uppercase tracking-wider text-sm w-28 text-[#00F0FF]">{route}</span>
                  {(snapshot.fields || []).map((f) => (
                    <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={cur.includes(f.id)} onChange={() => toggle(f.id)} data-testid={`display-${key}-field-${f.id}`} className="w-4 h-4 accent-[#00F0FF]" />
                      {f.name}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Sportart-Profil</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Modulares Regelwerk. Weitere Profile sind vorbereitet.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SPORTS.map((sp) => (
            <button
              key={sp.id}
              onClick={() => selectSport(sp.id, sp.available)}
              data-testid={`sport-${sp.id}`}
              className={`text-left rounded-xl border p-5 transition-all relative ${
                sport === sp.id ? "border-[#E5FF00] bg-[#0A0C10] shadow-[0_0_20px_rgba(229,255,0,0.2)]" : "border-[#1E2638] bg-[#0A0C10] hover:border-[#37456b]"
              } ${!sp.available ? "opacity-60" : ""}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold uppercase tracking-wide text-lg">{sp.name}</h3>
                {sport === sp.id && <Check className="w-5 h-5 text-[#E5FF00]" />}
              </div>
              <p className="text-sm text-[#8A99AD] mt-1">{sp.desc}</p>
              {!sp.available && <span className="inline-block mt-3 text-xs uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded px-2 py-0.5">Bald verfügbar</span>}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
